import { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { HiddenIcon, NotHiddenIcon } from "../../add-product";
import { useStyle } from "./style";
import { useTranslation } from "react-i18next";
import { DieMaterialItem, readDieSpecs } from "./gallery-die-display";

const SpecIcon = ({ children }: { children: JSX.Element }) => {
  const { clasess } = useStyle();
  return (
    <span style={clasess.specIcon} aria-hidden>
      {children}
    </span>
  );
};

const ShapeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="5.25" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const SizeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2.5" y="3.5" width="11" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2.5" y="2.5" width="4.2" height="4.2" rx="0.6" stroke="currentColor" strokeWidth="1.2" />
    <rect x="9.3" y="2.5" width="4.2" height="4.2" rx="0.6" stroke="currentColor" strokeWidth="1.2" />
    <rect x="2.5" y="9.3" width="4.2" height="4.2" rx="0.6" stroke="currentColor" strokeWidth="1.2" />
    <rect x="9.3" y="9.3" width="4.2" height="4.2" rx="0.6" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const SpacingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8H6.2M9.8 8H13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M4.2 6.2L2.4 8L4.2 9.8M11.8 6.2L13.6 8L11.8 9.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GalleryModalMapping = ({
  item,
  selectedShape,
  createParameterForCalculation,
  onClickChoosParameter,
  createParameterForCalculationHidden,
  isParameterSettings,
  isDiesParameter,
}: {
  item: DieMaterialItem;
  selectedShape: DieMaterialItem | null;
  createParameterForCalculation: (item: DieMaterialItem) => void;
  onClickChoosParameter: () => void;
  createParameterForCalculationHidden: (item: DieMaterialItem) => void;
  isParameterSettings?: boolean;
  isDiesParameter?: { valuesConfigs?: Array<{ id?: string; isHidden?: boolean }> };
}) => {
  const { clasess } = useStyle();
  const { t } = useTranslation();
  const specs = readDieSpecs(item);
  const [imageFailed, setImageFailed] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isSelected =
    item.id != null &&
    selectedShape?.id != null &&
    String(item.id) === String(selectedShape.id);
  const hiddenConfig = isDiesParameter?.valuesConfigs?.find((config) => config.id === item?.id);
  const title = specs.name || specs.sku || "";

  useEffect(() => {
    setImageFailed(false);
  }, [specs.imageSrc]);

  const specRows = [
    { key: "shape", icon: <ShapeIcon />, label: t("products.galleryModal.shape"), value: specs.shape },
    { key: "unitSize", icon: <SizeIcon />, label: t("products.galleryModal.unitSize"), value: specs.unitSize },
    { key: "dieSize", icon: <SizeIcon />, label: t("products.galleryModal.dieSize"), value: specs.dieSize },
    { key: "dieUnitSize", icon: <SizeIcon />, label: t("products.galleryModal.dieUnitSize"), value: specs.dieUnitSize },
    { key: "finalUnitSize", icon: <SizeIcon />, label: t("products.galleryModal.finalUnitSize"), value: specs.finalUnitSize },
    { key: "columnsLines", icon: <GridIcon />, label: t("products.galleryModal.columnsLines"), value: specs.columnsLines },
    { key: "spacing", icon: <SpacingIcon />, label: t("products.galleryModal.spacing"), value: specs.spacing },
    { key: "type", icon: <ShapeIcon />, label: t("products.galleryModal.type"), value: specs.type },
  ].filter((row) => row.value);

  const selectDie = () => createParameterForCalculation(item);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-controls={isSelected ? "gallery-die-details" : undefined}
      aria-label={
        isSelected
          ? `${title}. ${t("products.galleryModal.selected")}`
          : title
      }
      style={{
        ...(isSelected ? clasess.shapeSelectedContainer : clasess.shapeContainer),
        ...(isFocused ? clasess.shapeFocus : {}),
        ...(isParameterSettings ? { paddingBottom: 36 } : {}),
      }}
      onClick={selectDie}
      onDoubleClick={onClickChoosParameter}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          if (isSelected) {
            onClickChoosParameter();
          } else {
            selectDie();
          }
          return;
        }
        if (event.key === " ") {
          event.preventDefault();
          selectDie();
        }
      }}
    >
      <div style={clasess.imageWrap}>
        {specs.imageSrc && !imageFailed ? (
          <img
            src={specs.imageSrc}
            alt=""
            style={clasess.shapeImage}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div style={clasess.imagePlaceholder}>{t("products.galleryModal.noImage")}</div>
        )}
        {isSelected && (
          <span style={clasess.selectedBadge} aria-hidden>
            <CheckIcon sx={{ fontSize: 16 }} />
          </span>
        )}
      </div>
      <div style={clasess.cardBody}>
        <div style={clasess.titleRow}>
          <div style={clasess.shapeNameStyle}>{title}</div>
          {specs.sku && <span style={clasess.skuChip}>{specs.sku}</span>}
        </div>
        {specRows.length > 0 && (
          <div style={clasess.specList}>
            {specRows.map((row) => (
              <div key={row.key} style={clasess.specRow}>
                <SpecIcon>{row.icon}</SpecIcon>
                <span style={clasess.specLabel}>{row.label}</span>
                <span style={clasess.specValue}>{row.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {isParameterSettings && (
        <div style={clasess.hiddenToggle}>
          <Tooltip title={hiddenConfig?.isHidden ? "visible" : "hidden"}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                createParameterForCalculationHidden(item);
              }}
            >
              {hiddenConfig?.isHidden ? <HiddenIcon /> : <NotHiddenIcon />}
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export { GalleryModalMapping };
